import { Inject, Service } from "typedi";
import { DomMethods } from "../../../application/core/shared/dom-methods/DomMethods";
import { IGooglePayPaymentRequest } from "../../../integrations/google-pay/models/IGooglePayPaymentRequest";
import { IGooglePaySessionPaymentsClient } from "../../../integrations/google-pay/models/IGooglePaySessionPaymentsClient";
import { IConfig } from "../../../shared/model/config/IConfig";
import { type IInternalsMonitor } from "../../../application/core/services/monitoring/IInternalsMonitor";
import { GooglePayConfigMapper } from "./GooglePayConfigMapper";
import { GooglePayPaymentService } from "./GooglePayPaymentService";

export enum GooglePayButtonActionResult {
  SUCCESS = "SUCCESS",
  CANCELED = "CANCELED",
  ERROR = "ERROR",
}
@Service()
export class GooglePayButtonAction {
  private config: IConfig;
  constructor(
    private googlePayPaymentService: GooglePayPaymentService,
    @Inject("IInternalsMonitor") protected internalMonitor: IInternalsMonitor,
  ) {}

  async execute(
    config: IConfig,
    googlePaySdk: IGooglePaySessionPaymentsClient,
  ): Promise<GooglePayButtonActionResult> {
    this.config = config;

    const googleConfigMapper = new GooglePayConfigMapper();

    const paymentDataRequest: IGooglePayPaymentRequest =
      googleConfigMapper.createPaymentDataRequest(config);

    try {
      const paymentData = await googlePaySdk.loadPaymentData({
        ...paymentDataRequest,
        transactionInfo: { ...paymentDataRequest.transactionInfo },
      });

      this.onPaymentAuthorized(paymentData);
      return GooglePayButtonActionResult.SUCCESS;
    } catch (error: unknown) {
      if (typeof error === "object" && error !== null) {
        if ("statusCode" in error && error?.statusCode === "CANCELED") {
          this.onPaymentCancel();
          return GooglePayButtonActionResult.CANCELED;
        } else {
          this.onPaymentError();
          return GooglePayButtonActionResult.ERROR;
        }
      } else {
        this.onPaymentError();
        return GooglePayButtonActionResult.ERROR;
      }
    }
  }

  private onPaymentAuthorized(
    paymentData: google.payments.api.PaymentData,
  ): void {
    const formData = DomMethods.parseForm(this.config.formId);
    return this.googlePayPaymentService.processPayment(formData, paymentData);
  }

  private onPaymentCancel(): void {
    const formData = DomMethods.parseForm(this.config.formId);
    return this.googlePayPaymentService.cancelPayment(formData);
  }

  private onPaymentError(): void {
    const formData = DomMethods.parseForm(this.config.formId);
    return this.googlePayPaymentService.errorPayment(formData);
  }
}
